package com.hyperether.goodjob.scenes.addNew.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.Button
import androidx.compose.material.Divider
import androidx.compose.material.DropdownMenu
import androidx.compose.material.Text
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.toSize
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.is4K
import com.hyperether.goodjob.models.Team
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.assign_employee
import com.hyperether.goodjob.resources.assigned
import com.hyperether.goodjob.resources.confirm
import com.hyperether.goodjob.resources.select_team_or_user
import com.hyperether.goodjob.resources.teams
import com.hyperether.goodjob.resources.users_title
import com.hyperether.goodjob.scenes.components.TrailingDropdownIconButton
import com.hyperether.goodjob.scenes.components.disableMouseDragScrollOnWeb
import com.hyperether.goodjob.scenes.components.noRippleClickable
import com.hyperether.goodjob.scenes.components.verticalScrollbar
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.InputBorderColor
import com.hyperether.goodjob.theme.TextColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource


@Composable
fun AssignEmployeeDropdown(
    users: List<User>,
    teams: List<Team>,
    preSelectedUsers: List<User> = listOf(),
    preSelectedTeams: List<Team> = listOf(),
    onContactSelected: (List<User>, List<Team>) -> Unit,
    modifier: Modifier = Modifier,
    showLabel: Boolean = true,
) {
    // States to track selection
    var expanded by remember { mutableStateOf(false) }
    val textFieldSize = remember { mutableStateOf(Size.Zero) }

    val selectedUsersIds = remember { mutableStateListOf<String>() }
    val selectedTeamsIds = remember { mutableStateListOf<String>() }

    val usersScrollState = rememberScrollState()
    val teamsScrollState = rememberScrollState()

    LaunchedEffect(
        preSelectedUsers.mapNotNull { it._id },
        preSelectedTeams.mapNotNull { it._id }
    ) {
        val users = preSelectedUsers.mapNotNull { it._id }
        val teams = preSelectedTeams.mapNotNull { it._id }

        if (selectedUsersIds != users) {
            selectedUsersIds.clear()
            selectedUsersIds.addAll(users)
        }
        if (selectedTeamsIds != teams) {
            selectedTeamsIds.clear()
            selectedTeamsIds.addAll(teams)
        }
    }

    LaunchedEffect(expanded) {
        if (expanded) {
            usersScrollState.scrollTo(0)
            teamsScrollState.scrollTo(0)
        }
    }

    val buttonText = if (selectedUsersIds.isEmpty() && selectedTeamsIds.isEmpty()) {
        stringResource(Res.string.select_team_or_user)
    } else {
        stringResource(Res.string.assigned)
    }


    Box(
        modifier = modifier
            .fillMaxWidth()
            .noRippleClickable {
                expanded = !expanded
            }
    ) {
        OutlinedTextField(
            value = buttonText,
            onValueChange = {},
            singleLine = true,
            textStyle = TextStyle(fontSize = 14.toScaleSp()),
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor
            ),
            enabled = false,
            label = if (showLabel) {
                {
                    Text(
                        text = stringResource(Res.string.assign_employee),
                        fontSize = 14.toScaleSp(),
                        color = TextColor
                    )
                }
            } else null,
            modifier = if (!getPlatformChecker().isMobile() && is4K()) {
                Modifier
                    .fillMaxWidth()
                    .onGloballyPositioned { coordinates ->
                        textFieldSize.value = coordinates.size.toSize()
                    }
                    .height(56.toScaleDp())
            } else {
                Modifier
                    .fillMaxWidth()
                    .onGloballyPositioned { coordinates ->
                        textFieldSize.value = coordinates.size.toSize()
                    }
            },
            trailingIcon = {
                TrailingDropdownIconButton(onClick = { expanded = !expanded })
            }
        )

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = {
                selectedTeamsIds.clear()
                selectedTeamsIds.addAll(preSelectedTeams.map { it._id!! })
                selectedUsersIds.clear()
                selectedUsersIds.addAll(preSelectedUsers.map { it._id!! })
                expanded = false
            },
            modifier = Modifier.width(with(LocalDensity.current) { textFieldSize.value.width.toDp() })
        ) {
            Button(
                colors = androidx.compose.material.ButtonDefaults.buttonColors(BlueApp),
                shape = RoundedCornerShape(32.toScaleDp()),
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(
                        start = 32.toScaleDp(),
                        top = 8.toScaleDp(),
                        end = 32.toScaleDp(),
                        bottom = 8.toScaleDp()
                    )
                    .pointerHoverIcon(PointerIcon.Hand),
                onClick = {
                    expanded = !expanded
                    onContactSelected(
//                        selectedUsers.filter { it.value }.keys.toList(),
//                        selectedTeams.filter { it.value }.keys.toList()
                        users.filter { selectedUsersIds.contains(it._id) },
                        teams.filter { selectedTeamsIds.contains(it._id) }
                    )
                }) {
                DisableSelection {
                    Text(
                        text = stringResource(Res.string.confirm),
                        modifier = Modifier.padding(vertical = 8.toScaleDp()),
                        color = Color.White,
                        fontSize = 14.toScaleSp(),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }

            Text(
                text = stringResource(Res.string.users_title),
                modifier = Modifier.padding(8.toScaleDp()),
                fontSize = 14.toScaleSp(),
                fontWeight = FontWeight.W700,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(max = 200.toScaleDp())
                    .verticalScrollbar(usersScrollState)
                    .verticalScroll(usersScrollState)
                    .disableMouseDragScrollOnWeb()
            ) {
                users.forEach { user ->
                    DropdownMenuItem(
                        modifier = Modifier
                            .height(45.toScaleDp())
                            .pointerHoverIcon(PointerIcon.Hand),
                        text = {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                androidx.compose.material3.Checkbox(
                                    modifier = Modifier.scale(1f),
                                    checked = selectedUsersIds.contains(user._id),
                                    onCheckedChange = null,
                                    colors = CheckboxDefaults.colors(
                                        checkedColor = BlueApp,
                                        uncheckedColor = com.hyperether.goodjob.theme.Divider
                                    )
                                )
                                Spacer(modifier = Modifier.width(8.toScaleDp()))
                                DisableSelection {
                                    Text(
                                        text = user.getName(),
                                        maxLines = 1,
                                        fontSize = 14.toScaleSp(),
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                            }
                        },
                        onClick = {
                            val isCurrentlySelected = selectedUsersIds.contains(user._id)
                            if (isCurrentlySelected) {
                                selectedUsersIds.remove(user._id)
                            } else {
                                selectedUsersIds.add(user._id!!)
                            }
                        }
                    )
                }
            }

            if (teams.isNotEmpty()) {
                Divider()
                // Team List Section
                Text(
                    text = stringResource(Res.string.teams),
                    modifier = Modifier.padding(8.toScaleDp()),
                    fontSize = 14.toScaleSp(),
                    fontWeight = FontWeight.W700,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(max = 200.toScaleDp())
                        .verticalScrollbar(teamsScrollState)
                        .verticalScroll(teamsScrollState)
                        .disableMouseDragScrollOnWeb()
                ) {
                    teams.forEach { team ->
                        DropdownMenuItem(
                            modifier = Modifier
                                .height(45.toScaleDp())
                                .pointerHoverIcon(PointerIcon.Hand),
                            text = {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    androidx.compose.material3.Checkbox(
                                        modifier = Modifier.scale(1f),
                                        checked = selectedTeamsIds.contains(team._id),
                                        onCheckedChange = null,
                                        colors = CheckboxDefaults.colors(
                                            checkedColor = BlueApp,
                                            uncheckedColor = com.hyperether.goodjob.theme.Divider
                                        )
                                    )
                                    Spacer(modifier = Modifier.width(8.toScaleDp()))
                                    DisableSelection {
                                        Text(
                                            text = team.name ?: "",
                                            maxLines = 1,
                                            fontSize = 14.toScaleSp(),
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                }
                            },
                            onClick = {
                                val isCurrentlySelected = selectedTeamsIds.contains(team._id)
                                if (isCurrentlySelected) {
                                    selectedTeamsIds.remove(team._id)
                                } else {
                                    selectedTeamsIds.add(team._id!!)
                                }
                            }
                        )
                    }
                }
            }
        }
    }
}
